import { __assign, __spreadArray } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { debounce } from 'throttle-debounce';
import { classNames, analyticsTracker, liveAnnounce, MakeEgdsSizingLength, Level, UitkContextProvider } from 'uitk-react-utils';
import styles from './uitk-carousel.css';
import { UitkHeadingTags } from './types.esm.js';
import { UitkCarouselItemList } from './uitk-carousel-item-list.esm.js';
import { UitkCarouselNav } from './uitk-carousel-nav.esm.js';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
var getClassNameList = classNames.bind(styles);
var UitkCarouselLocalAnalyticsIdType;
(function (UitkCarouselLocalAnalyticsIdType) {
  UitkCarouselLocalAnalyticsIdType["clickLeft"] = "clickLeft";
  UitkCarouselLocalAnalyticsIdType["clickRight"] = "clickRight";
  UitkCarouselLocalAnalyticsIdType["scrollLeft"] = "scrollLeft";
  UitkCarouselLocalAnalyticsIdType["scrollRight"] = "scrollRight";
})(UitkCarouselLocalAnalyticsIdType || (UitkCarouselLocalAnalyticsIdType = {}));
var Carousel = function (props) {
  var analytics = props.analytics,
    buttonText = props.buttonText,
    carouselHeaderLevel = props.carouselHeaderLevel,
    carouselHeaderText = props.carouselHeaderText,
    carouselHeaderVisible = props.carouselHeaderVisible,
    carouselName = props.carouselName,
    carouselRef = props.carouselRef,
    children = props.children,
    className = props.className,
    domRef = props.domRef,
    fullSheetVisible = props.fullSheetVisible,
    _a = props.hideNav,
    hideNav = _a === void 0 ? false : _a,
    innerSpacing = props.innerSpacing,
    _b = props.itemsGap,
    itemsGap = _b === void 0 ? {
      sm: 16,
      md: 16,
      lg: 16
    } : _b,
    _c = props.itemsVisible,
    itemsVisible = _c === void 0 ? {
      sm: 1,
      md: 2,
      lg: 3,
      xl: 4
    } : _c,
    _d = props.itemsMaxHeight,
    itemsMaxHeight = _d === void 0 ? false : _d,
    l10n = props.l10n,
    _e = props.naturalWidthItems,
    naturalWidthItems = _e === void 0 ? false : _e,
    _f = props.navigationIconId,
    navigationIconId = _f === void 0 ? {
      next: 'next-button',
      prev: 'prev-button'
    } : _f,
    onChange = props.onChange,
    onIndexChange = props.onIndexChange,
    _g = props.pagingInset,
    pagingInset = _g === void 0 ? false : _g,
    _h = props.pagingOverlay,
    pagingOverlay = _h === void 0 ? false : _h,
    pagingPersistent = props.pagingPersistent,
    _j = props.peek,
    peek = _j === void 0 ? false : _j,
    _k = props.peekAmount,
    peekAmount = _k === void 0 ? {
      sm: 48,
      md: 16,
      lg: 32
    } : _k,
    _l = props.startIndex,
    startIndex = _l === void 0 ? 0 : _l,
    _m = props.stretchToFill,
    stretchToFill = _m === void 0 ? true : _m,
    _o = props.useContainerSizing,
    useContainerSizing = _o === void 0 ? true : _o,
    _p = props.wrapItems,
    wrapItems = _p === void 0 ? true : _p;
  var _q = React.useState(false),
    mounted = _q[0],
    setMounted = _q[1];
  var _r = React.useState(0),
    currentIndex = _r[0],
    setCurrentIndex = _r[1];
  var _s = React.useState(false),
    isTouch = _s[0],
    setIsTouch = _s[1];
  var _t = React.useState(false),
    isRtl = _t[0],
    setIsRtl = _t[1];
  var _u = React.useState('sm'),
    size = _u[0],
    setSize = _u[1];
  var _v = React.useState(16),
    itemsGapAmount = _v[0],
    setItemsGapAmount = _v[1];
  var _w = React.useState(48),
    peekWidthAmount = _w[0],
    setPeekWidthAmount = _w[1];
  var _x = React.useState(0),
    innerSpacingAmount = _x[0],
    setInnerSpacingAmount = _x[1];
  var _y = React.useState(false),
    sizeInitialized = _y[0],
    setSizeInitialized = _y[1];
  var _z = React.useState(false),
    isFullyScrolled = _z[0],
    setIsFullyScrolled = _z[1];
  var _0 = React.useState({
      PARTIAL: false,
      FULL: false
    }),
    carouselVisibility = _0[0],
    setCarouselVisibility = _0[1];
  var _1 = React.useState(0),
    windowWidth = _1[0],
    setWindowWidth = _1[1];
  var _2 = React.useState(false),
    scrollEventOngoing = _2[0],
    setScrollEventOngoing = _2[1];
  // Below values are reffed because we want them to be maintained between renders
  var NAVIGATE_WITH_BTN = React.useRef(false); // Scrolling using a paging button
  var SCROLL_FROM_RESIZE_EVENT = React.useRef(false); // Scrolling as a result of a resizing event
  var AVOID_FREE_SCROLL = React.useRef(false); // Free scrolling is scrolling the carousel without using the paging buttons
  var CHANGE_INDEX_WITHOUT_ANIMATION = React.useRef(false);
  var SCROLL_WITHOUT_ANIMATION = React.useRef(false);
  var WRAPPER = React.useRef(null);
  var containerRef = React.useRef(null);
  var viewed = React.useRef([]);
  // Distance from origin of the left-most item (or right-most item for RTL)
  var positionWithinCarousel = React.useRef(0);
  // We use this to know if the carousel is going forwards or backwards
  var directionOfScrollIsForward = React.useRef(true);
  // Carousel settings and shorthands
  var INTERACTION_TIMER = 200; // Interaction Timer of resize handler
  var SCROLL_TIMER = 350;
  var NATURAL_WIDTH_ITEMS_GAP_DEFAULT = 12;
  var itemCount = React.Children.count(children);
  var wrapperRef = domRef !== null && domRef !== void 0 ? domRef : WRAPPER;
  var currentItemsVisible = itemsVisible[size]; // This can be non-nullable, since setViewportSize will always set a valid size
  var fullImageGalleryType = !naturalWidthItems && currentItemsVisible === 1 && !peek;
  var edgeToEdgeType = peek && peekAmount && innerSpacing;
  var reverseRtlValue = function (value) {
    return isRtl && value !== 0 ? value * -1 : value;
  }; // The scrollLeft method returns a negative value in RTL but our functions frequently expect a positive (or reverse) value
  var snapPointsArrayRef = React.useRef([]);
  var initialSnapPointItems = [];
  // This reducer function is fed to carousel items and updated via the UITK Context Provider,
  // in order to determine child offsetLeft amounts and update 'snap points' array for items
  var getSnapPointItemsReducer = function (state, action) {
    var newItem = action.payload;
    var itemIndex = newItem.itemIndex,
      snapPoint = newItem.snapPoint;
    var calculateSnapPointFromItemWidth = function () {
      var _a;
      var prevSnapPoint = (_a = state.find(function (stateItem) {
        return stateItem.itemIndex === itemIndex - 1;
      })) === null || _a === void 0 ? void 0 : _a.snapPoint;
      if (itemIndex === 0) {
        return innerSpacingAmount || 0;
      }
      return snapPoint + itemsGapAmount + (prevSnapPoint || 0);
    };
    // In RTL mode, we cannot rely on offsetLeft so are relying on the item's width
    var newItemSnapPoint = isRtl ? calculateSnapPointFromItemWidth() : Math.floor(snapPoint);
    var existingIndex = state.findIndex(function (existingStateItem) {
      return existingStateItem.itemIndex === newItem.itemIndex;
    });
    if (existingIndex !== -1) {
      var updatedState = state.map(function (stateItem, stateItemIndex) {
        return stateItemIndex === itemIndex ? __assign(__assign({}, stateItem), {
          snapPoint: newItemSnapPoint
        }) : stateItem;
      });
      return updatedState;
    }
    newItem.snapPoint = newItemSnapPoint;
    var appendedState = __spreadArray(__spreadArray([], state, true), [newItem], false);
    return appendedState;
  };
  var _3 = React.useReducer(getSnapPointItemsReducer, initialSnapPointItems),
    snapPointItems = _3[0],
    snapPointItemsDispatch = _3[1];
  // This reducer function is fed to carousel items and updated via the UITK Context Provider,
  // in order to track which natural width items are currently in view, for liveAnnounce/A11y purposes
  /* istanbul ignore next */
  var _4 = React.useReducer(function (state, action) {
      var newItemInView = action.payload;
      var existingIndex = state.findIndex(function (itemObj) {
        return itemObj.itemIndex === newItemInView.itemIndex;
      });
      if (existingIndex !== -1) {
        var updatedState = state.map(function (stateItem, stateItemIndex) {
          return stateItemIndex === newItemInView.itemIndex ? __assign(__assign({}, stateItem), {
            inView: newItemInView.inView
          }) : stateItem;
        });
        return updatedState.sort(function (a, b) {
          return a.itemIndex - b.itemIndex;
        });
      }
      var appendedState = __spreadArray(__spreadArray([], state, true), [newItemInView], false);
      return appendedState.sort(function (a, b) {
        return a.itemIndex - b.itemIndex;
      });
    }, []),
    naturalWidthItemsInView = _4[0],
    naturalWidthItemsInViewDispatch = _4[1];
  // This reducer function is fed to carousel items and updated via the UITK Context Provider,
  // in order to track which natural width items have been viewed, for analytics purposes
  /* istanbul ignore next */
  var _5 = React.useReducer(function (state, action) {
      var newItemViewed = action.payload;
      var existingIndex = state.findIndex(function (itemObj) {
        return itemObj.itemIndex === newItemViewed.itemIndex;
      });
      var newState = existingIndex === -1 ? __spreadArray(__spreadArray([], state, true), [newItemViewed], false) : state;
      return newState.sort(function (a, b) {
        return a.itemIndex - b.itemIndex;
      });
    }, []),
    naturalWidthItemsViewed = _5[0],
    naturalWidthItemsViewedDispatch = _5[1];
  var findSnapPointIndex = function (target) {
    // Binary search locates the index of the snap point closest to a target number
    if (snapPointsArrayRef.current.length > 0) {
      var n = snapPointsArrayRef.current.length;
      var snapPointsArray = Object.values(snapPointsArrayRef.current).map(function (item) {
        return item.snapPoint;
      });
      var left = 0;
      var right = n - 1;
      while (left < right) {
        if (Math.abs(snapPointsArray[left] - target) <= Math.abs(snapPointsArray[right] - target)) {
          right -= 1;
        } else {
          left += 1;
        }
      }
      return snapPointsArray.indexOf(snapPointsArray[left]);
    }
    return 0;
  };
  // Snap Point generation functions -- Ends
  // A11y related functions -- Starts
  var announceCards = function () {
    if (containerRef.current) {
      var visibleCardHeadingsElements = containerRef.current.querySelectorAll("\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h1:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h2:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h3:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h4:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h5:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h6:not([aria-hidden=\"true\"])\n        ");
      var visibleCardHeadings_1 = Array.from(visibleCardHeadingsElements).map(function (heading) {
        return heading.innerText || heading.textContent;
      });
      var naturalWidthItemsCurrentlyVisibleIndices_1 = naturalWidthItemsInView.filter(function (item) {
        return item.inView;
      }).map(function (item) {
        return item.itemIndex + 1;
      });
      if (naturalWidthItems) {
        setTimeout(function () {
          liveAnnounce(l10n.formatText('carousel.indexAndTotal', naturalWidthItemsCurrentlyVisibleIndices_1.join(', '), itemCount, ''), Level.POLITE);
        }, 200);
      } else if (visibleCardHeadings_1.length > 0) {
        setTimeout(function () {
          liveAnnounce(l10n.formatText('carousel.cardChange', visibleCardHeadings_1.join(', ')), Level.POLITE);
        }, 200);
      } else {
        setTimeout(function () {
          liveAnnounce(l10n.formatText('gallerycarousel.imageChange', currentIndex + 1, itemCount, ''), Level.POLITE);
        }, 200);
      }
    }
  };
  // A11y related functions -- Ends
  // Analytics functions -- Starts
  var viewItemAnalytics = function () {
    var setAnalyticsTrackingValues = function (itemIndex) {
      if (!viewed.current[itemIndex]) {
        viewed.current[itemIndex] = true;
        if (typeof props.itemViewedCallback === 'function') {
          props.itemViewedCallback(itemIndex);
        }
        analytics && analyticsTracker(analytics, "".concat(carouselName, ".Carousel.itemViewed.").concat(itemIndex), '');
      }
    };
    if (naturalWidthItems) {
      naturalWidthItemsViewed.forEach(function (itemViewed) {
        setAnalyticsTrackingValues(itemViewed.itemIndex);
      });
    } else {
      var vis = currentItemsVisible;
      if (peek) {
        vis += 1;
      }
      for (var i = 0; i < vis && currentIndex + i < React.Children.count(children); ++i) {
        setAnalyticsTrackingValues(currentIndex + i);
      }
    }
  };
  var scrollAnalytics = React.useCallback(function (scrollRight) {
    analytics && analyticsTracker(analytics, "".concat(carouselName, ".Carousel.").concat(scrollRight ? UitkCarouselLocalAnalyticsIdType.scrollRight : UitkCarouselLocalAnalyticsIdType.scrollLeft), '');
  }, [analytics, carouselName]);
  // Analytics functions -- Ends
  // Carousel main functions -- Starts
  var getCurrentIndex = function (currentPosition) {
    if (containerRef.current) {
      // FullImageGalleryType does not have the snap points array populated during scroll (unknown why),
      // so we need to rely on a manual calculation in this case
      if (fullImageGalleryType) {
        var carouselWidth = containerRef.current.getBoundingClientRect().width;
        var distanceFromOrigin = Math.abs(currentPosition);
        var calculatedCurrentIndex = Math.round(distanceFromOrigin / carouselWidth);
        return calculatedCurrentIndex;
      }
      return findSnapPointIndex(currentPosition);
    }
    return 0;
  };
  var getCurrentIndexForNaturalWidthItems = function (currentPosition) {
    if (containerRef.current) {
      var targetIndex = findSnapPointIndex(currentPosition);
      return targetIndex;
    }
    return 0;
  };
  var checkIsFullyScrolled = function () {
    var containerElement = containerRef.current;
    if (containerElement) {
      var scrollLeft = containerElement.scrollLeft,
        clientWidth = containerElement.clientWidth,
        scrollWidth = containerElement.scrollWidth;
      var maxIncrementPoint = scrollWidth - clientWidth - 1;
      setIsFullyScrolled(reverseRtlValue(scrollLeft) >= maxIncrementPoint);
    }
  };
  /* istanbul ignore next */
  var getIncrementalPosition = function (time, positionToSnapTo, positionUserScrolledTo, range) {
    // Quadratic easein-easeout function // refer to https://easings.net/#easeInOutQuad
    var curve = function (x) {
      return x < 0.5 ? 2 * Math.pow(x, 2) : 1 - Math.pow(-2 * x + 2, 2) / 2;
    };
    var done = time >= SCROLL_TIMER || SCROLL_WITHOUT_ANIMATION.current;
    var position = done ? positionToSnapTo : positionUserScrolledTo + range * curve(time / SCROLL_TIMER);
    return {
      done: done,
      position: position
    };
  };
  // Scroll carousel to newPosition pixels from origin (which is the left edge for LTR and the right edge for RTL)
  var scrollCarousel = function (newPosition) {
    var startTimestamp;
    if (containerRef.current) {
      !CHANGE_INDEX_WITHOUT_ANIMATION.current && setScrollEventOngoing(true);
      var containerElement_1 = containerRef.current;
      var positionUserCurrentlyScrolledTo_1 = reverseRtlValue(containerElement_1.scrollLeft);
      var range_1 = newPosition - positionUserCurrentlyScrolledTo_1;
      // Incrementally advance the carousel using the curve value described above
      // istanbul ignore next
      var step_1 = function (timestamp) {
        if (!startTimestamp) {
          startTimestamp = timestamp;
        }
        var _a = getIncrementalPosition(timestamp - startTimestamp, newPosition, positionUserCurrentlyScrolledTo_1, range_1),
          done = _a.done,
          position = _a.position;
        containerElement_1.scrollLeft = fullImageGalleryType ? reverseRtlValue(newPosition) : reverseRtlValue(position); // This moves the carousel
        if (done || fullImageGalleryType) {
          checkIsFullyScrolled();
          setScrollEventOngoing(false);
          !SCROLL_FROM_RESIZE_EVENT.current && scrollAnalytics(range_1 > 0);
        } else {
          window.requestAnimationFrame(step_1);
        }
      };
      if (CHANGE_INDEX_WITHOUT_ANIMATION.current) {
        // Occurs when startIndex is set
        containerElement_1.scrollLeft = reverseRtlValue(newPosition);
        checkIsFullyScrolled();
        setMounted(true);
        CHANGE_INDEX_WITHOUT_ANIMATION.current = false;
      } else {
        window.requestAnimationFrame(step_1);
      }
      positionWithinCarousel.current = newPosition;
    }
  };
  var incrementIndexAsNeeded = function (index) {
    /* istanbul ignore else */
    if (directionOfScrollIsForward.current === true) {
      return index + 1;
    }
    return index;
  };
  var setScrollingForwardRef = function (position) {
    if (position > positionWithinCarousel.current) {
      directionOfScrollIsForward.current = true;
    } else {
      directionOfScrollIsForward.current = false;
    }
  };
  var getIndexToSet = function (index) {
    var maxIndex = itemCount - currentItemsVisible;
    var newIndex = incrementIndexAsNeeded(index);
    if (newIndex > maxIndex) {
      return maxIndex;
    }
    return newIndex;
  };
  // Adding this ignore comment to prevent this issue https://jira.expedia.biz/browse/EGDS-3032
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var onScroll = React.useCallback(debounce(INTERACTION_TIMER, function () {
    if (AVOID_FREE_SCROLL.current) {
      AVOID_FREE_SCROLL.current = false;
      return;
    }
    if (NAVIGATE_WITH_BTN.current) {
      NAVIGATE_WITH_BTN.current = false;
      return;
    }
    CHANGE_INDEX_WITHOUT_ANIMATION.current = false;
    if (containerRef.current) {
      var positionUserScrolledTo = reverseRtlValue(containerRef.current.scrollLeft);
      setScrollingForwardRef(positionUserScrolledTo);
      var isRightScroll = positionUserScrolledTo - positionWithinCarousel.current > 0;
      scrollAnalytics(isRightScroll);
      positionWithinCarousel.current = positionUserScrolledTo;
      if (SCROLL_FROM_RESIZE_EVENT.current) {
        SCROLL_FROM_RESIZE_EVENT.current = false;
      } else {
        var currentIndexAfterScroll = naturalWidthItems ? getCurrentIndexForNaturalWidthItems(positionUserScrolledTo) : getCurrentIndex(positionUserScrolledTo);
        var indexToSet = fullImageGalleryType ? currentIndexAfterScroll : getIndexToSet(currentIndexAfterScroll);
        setCurrentIndex(indexToSet);
      }
      checkIsFullyScrolled();
    }
  }), [size, isRtl]);
  // Prevents tab events from activating scrolling
  var onKeyboardEvent = React.useCallback(function (e) {
    if (e.key) {
      NAVIGATE_WITH_BTN.current = true;
      AVOID_FREE_SCROLL.current = false;
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [size, isRtl]);
  React.useEffect(function () {
    document.body.addEventListener('keydown', onKeyboardEvent);
    return function () {
      document.body.removeEventListener('keydown', onKeyboardEvent);
    };
  }, [onKeyboardEvent]);
  var getScrollPointForIndex = function (index) {
    var _a, _b;
    if (!peek || edgeToEdgeType) {
      return ((_a = snapPointItems[index]) === null || _a === void 0 ? void 0 : _a.snapPoint) - innerSpacingAmount;
    }
    if (peek && !edgeToEdgeType) {
      var snapPointAdjustment = currentItemsVisible > 1 ? peekWidthAmount - itemsGapAmount - innerSpacingAmount : 0;
      return ((_b = snapPointItems[index]) === null || _b === void 0 ? void 0 : _b.snapPoint) - snapPointAdjustment;
    }
  };
  var checkNavigationShouldBeVisible = function () {
    if (naturalWidthItems) {
      var containerElement = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
      if (containerElement) {
        var carouselWidth = containerElement.getBoundingClientRect().width;
        var scrollWidth = containerElement.scrollWidth;
        return Math.ceil(carouselWidth) < scrollWidth;
      }
    } else {
      return itemCount > currentItemsVisible;
    }
  };
  var adjustLayoutSettingsByCarouselSize = function (carouselSize) {
    // Override prop setting for natural width items
    var itemsGapLookup = naturalWidthItems ? NATURAL_WIDTH_ITEMS_GAP_DEFAULT : itemsGap && itemsGap[carouselSize];
    var innerSpacingAmountLookup = innerSpacing && innerSpacing[carouselSize];
    var peekWidthAmountLookup = peek && peekAmount && peekAmount[carouselSize];
    // If innerSpacing is set, add it to peekWidthAmount
    var calculatedPeekWidthAmount = innerSpacingAmountLookup ? peekWidthAmountLookup + innerSpacingAmountLookup : peekWidthAmountLookup;
    itemsGapLookup && setItemsGapAmount(Number(MakeEgdsSizingLength(itemsGapLookup, false)));
    calculatedPeekWidthAmount && setPeekWidthAmount(Number(MakeEgdsSizingLength(calculatedPeekWidthAmount, false)));
    innerSpacingAmountLookup && setInnerSpacingAmount(Number(MakeEgdsSizingLength(innerSpacingAmountLookup, false)));
  };
  var setViewportSize = function () {
    var containerElement = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
    window && setWindowWidth(window.innerWidth);
    if (containerElement) {
      var carouselSize = getComputedStyle(containerElement).getPropertyValue('--size') || 'sm';
      if (carouselSize === 'xl' && !itemsVisible.xl) {
        carouselSize = 'lg'; // Backward compatibility if xl is not passed
      }
      if (carouselSize) {
        adjustLayoutSettingsByCarouselSize(carouselSize);
      }
      setSize(carouselSize);
      if (!sizeInitialized) setSizeInitialized(true);
    }
  };
  var page = function (direction) {
    var _a, _b;
    SCROLL_FROM_RESIZE_EVENT.current = false;
    CHANGE_INDEX_WITHOUT_ANIMATION.current = false;
    if (naturalWidthItems) {
      var containerElement = containerRef.current;
      if (containerElement) {
        var carouselWidth = containerElement.getBoundingClientRect().width;
        var pagingDistance = carouselWidth * 0.75;
        var currentPosition = reverseRtlValue(containerElement.scrollLeft);
        var clientWidth = containerElement.clientWidth,
          scrollWidth = containerElement.scrollWidth;
        var maxIncrementPoint = scrollWidth - clientWidth - innerSpacingAmount * 2;
        var targetPosition = direction === 1 ? currentPosition + pagingDistance : currentPosition - pagingDistance;
        var isAboutToHitMax = direction === 1 && targetPosition > maxIncrementPoint;
        var targetIndex = findSnapPointIndex(targetPosition);
        var maxIndex = findSnapPointIndex(maxIncrementPoint) + 1;
        if (isAboutToHitMax) {
          setCurrentIndex(maxIndex);
          // Solves for edge case when carousel is scrolled 'nearly' to the end;
          // bypass typical paging behavior and scroll to the end
          !isFullyScrolled && direction === 1 && scrollCarousel((_a = snapPointItems[maxIndex]) === null || _a === void 0 ? void 0 : _a.snapPoint);
        } else {
          setCurrentIndex(targetIndex);
        }
      }
    } else {
      var capPageBy = currentItemsVisible;
      var index = direction === 1 ? currentIndex + Number(capPageBy) : currentIndex - Number(capPageBy);
      var limit = itemCount - currentItemsVisible;
      if (index < 0) {
        index = 0;
      } else if (index >= limit) {
        index = limit;
      }
      setCurrentIndex(index);
      // Solves for edge case when carousel is scrolled 'nearly' to the end;
      // bypass typical paging behavior and scroll to the end
      if (index === limit && !isFullyScrolled && direction === 1) {
        scrollCarousel((_b = snapPointItems[index]) === null || _b === void 0 ? void 0 : _b.snapPoint);
      }
    }
    NAVIGATE_WITH_BTN.current = true;
  };
  var onPrevCb = function () {
    page(-1);
  };
  var onNextCb = function () {
    page(1);
  };
  // Changes the carousel's current index
  var changeIndex = function (index) {
    // This is set to bypass scroll event callbacks
    NAVIGATE_WITH_BTN.current = true;
    var newIndex;
    var maxIndex;
    if (naturalWidthItems && containerRef.current) {
      var _a = containerRef.current,
        clientWidth = _a.clientWidth,
        scrollWidth = _a.scrollWidth;
      var maxIncrementPoint = scrollWidth - clientWidth - innerSpacingAmount * 2;
      maxIndex = findSnapPointIndex(maxIncrementPoint) + 1;
    } else {
      maxIndex = itemCount - currentItemsVisible;
    }
    if (index < 0) {
      newIndex = 0;
    } else if (index >= maxIndex) {
      newIndex = maxIndex;
    } else {
      newIndex = index;
    }
    setCurrentIndex(newIndex);
  };
  // Hooks
  React.useEffect(function () {
    mounted && announceCards();
    if (sizeInitialized && carouselVisibility.FULL) viewItemAnalytics();
    // Turn off free scroll and yet advance the carousel
    if (NAVIGATE_WITH_BTN.current) {
      SCROLL_FROM_RESIZE_EVENT.current = false;
      NAVIGATE_WITH_BTN.current = false;
      AVOID_FREE_SCROLL.current = true;
      var scrollPoint = getScrollPointForIndex(currentIndex);
      scrollCarousel(scrollPoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselVisibility.FULL, currentIndex, naturalWidthItemsInView, naturalWidthItemsViewed, sizeInitialized]);
  React.useEffect(function () {
    onIndexChange && onIndexChange(currentIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);
  React.useEffect(function () {
    // Ref the snap points of the carousel so that they don't get blasted away on scroll, as can happen with state
    snapPointsArrayRef.current = snapPointItems;
  }, [snapPointItems]);
  React.useEffect(function () {
    // Initialize viewport size
    setViewportSize();
    // Ensure that scrollLeft position is reset on mount
    if (!startIndex && containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
    // Change carousel's index to startIndex without triggering scroll animation
    if (startIndex) {
      CHANGE_INDEX_WITHOUT_ANIMATION.current = true;
      changeIndex(startIndex);
    } else {
      setMounted(true);
    }
    'ontouchstart' in window && setIsTouch(true);
    document.querySelector('html').getAttribute('dir') === 'rtl' && setIsRtl(true);
    // Adds empty aria-live region to page to ensure Jaws screen reader
    // announces on initial key press. Relates to https://jira.expedia.biz/browse/WCAG-8258
    liveAnnounce('');
    if (sizeInitialized && carouselVisibility.FULL) viewItemAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeInitialized]);
  React.useEffect(function () {
    window && setWindowWidth(window.innerWidth);
    var debounceResizeHandler = debounce(INTERACTION_TIMER, function () {
      if (windowWidth === window.innerWidth) {
        return;
      }
      setWindowWidth(window.innerWidth);
      setViewportSize();
    });
    window.addEventListener('resize', debounceResizeHandler);
    return function () {
      return window.removeEventListener('resize', debounceResizeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, currentIndex]);
  React.useEffect(function () {
    var mediaQuery = window && window.matchMedia('(prefers-reduced-motion: reduce)');
    SCROLL_WITHOUT_ANIMATION.current = !mediaQuery || mediaQuery.matches;
    mediaQuery.addEventListener('change', function () {
      SCROLL_WITHOUT_ANIMATION.current = mediaQuery.matches;
    });
  }, []);
  React.useEffect(function () {
    var _a;
    // Ensures that upon resize, the carousel will scroll to a position where the updated index slide will completely show.
    // Natural width item-type carousels do not need this, since their dimensions don't change on resize
    if (!naturalWidthItems && !CHANGE_INDEX_WITHOUT_ANIMATION.current) {
      var currentPosition = reverseRtlValue((_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _a === void 0 ? void 0 : _a.scrollLeft);
      var updatedCurrentIndexOnResize = getCurrentIndex(currentPosition);
      /* istanbul ignore next */
      if (updatedCurrentIndexOnResize > 0 && snapPointItems) {
        SCROLL_FROM_RESIZE_EVENT.current = true;
        var scrollPointAtNewIndex = getScrollPointForIndex(updatedCurrentIndexOnResize);
        if (scrollPointAtNewIndex) {
          setCurrentIndex(updatedCurrentIndexOnResize);
          scrollCarousel(scrollPointAtNewIndex);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, windowWidth, snapPointItems]);
  React.useEffect(function () {
    onChange && onChange(containerRef);
  });
  React.useEffect(function () {
    // Used to determine if carousel is fully visible and trigger analytics for visible items accordingly
    var intersectionObserverOptions = {
      threshold: [0, 1],
      root: null
    };
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        setCarouselVisibility({
          PARTIAL: entry.isIntersecting,
          FULL: entry.intersectionRatio === 1
        });
      });
    }, intersectionObserverOptions);
    var containerRefCurrent = containerRef.current;
    containerRefCurrent && observer.observe(containerRefCurrent);
    return function () {
      containerRefCurrent && observer.unobserve(containerRefCurrent);
    };
  }, []);
  var contextData = React.useMemo(function () {
    return {
      isNaturalWidthItemsCarousel: naturalWidthItems,
      carouselWindowWidth: windowWidth,
      carouselSize: size,
      setSnapPointItem: snapPointItemsDispatch,
      setNaturalWidthItemsInView: naturalWidthItemsInViewDispatch,
      setNaturalWidthItemsViewed: naturalWidthItemsViewedDispatch,
      isRtl: isRtl,
      carouselIsVisible: carouselVisibility.PARTIAL
    };
  }, [naturalWidthItems, windowWidth, isRtl, size, carouselVisibility.PARTIAL]);
  var roundedItemsGap = React.useMemo(function () {
    return MakeEgdsSizingLength(itemsGap[size]);
  }, [size, itemsGap]);
  var fullPeekAmountObj = React.useMemo(function () {
    return {
      sm: peekAmount.sm + ((innerSpacing === null || innerSpacing === void 0 ? void 0 : innerSpacing.sm) || 0),
      md: peekAmount.md + ((innerSpacing === null || innerSpacing === void 0 ? void 0 : innerSpacing.md) || 0),
      lg: peekAmount.lg + ((innerSpacing === null || innerSpacing === void 0 ? void 0 : innerSpacing.lg) || 0),
      xl: peekAmount.xl ? peekAmount.xl + ((innerSpacing === null || innerSpacing === void 0 ? void 0 : innerSpacing.xl) || 0) : peekAmount.lg + ((innerSpacing === null || innerSpacing === void 0 ? void 0 : innerSpacing.lg) || 0)
    };
  }, [peekAmount, innerSpacing]);
  var getCssCustomPropertiesForBreakpointBasedProp = React.useCallback(function (customPropExcerpt, breakPointValuesObj, returnRoundedPxValue) {
    if (returnRoundedPxValue === void 0) {
      returnRoundedPxValue = true;
    }
    var customPropsObj = {};
    Object.keys(breakPointValuesObj).forEach(function (breakpoint) {
      var value = breakPointValuesObj[breakpoint];
      var formattedValue = returnRoundedPxValue ? "".concat(MakeEgdsSizingLength(value)) : value;
      customPropsObj["--uitk-carousel-".concat(customPropExcerpt, "-").concat(breakpoint)] = formattedValue;
    });
    return customPropsObj;
  }, []);
  var getGapCssCustomProp = React.useCallback(function () {
    if (fullImageGalleryType) {
      return '0px';
    }
    if (naturalWidthItems) {
      return "".concat(NATURAL_WIDTH_ITEMS_GAP_DEFAULT, "px");
    }
    return roundedItemsGap;
  }, [roundedItemsGap, fullImageGalleryType, naturalWidthItems]);
  var renderCarousel = function () {
    var _a;
    var cssClassNames = getClassNameList('uitk-carousel', (!peek || !(currentItemsVisible < itemCount)) && 'no-peek', !pagingInset && 'no-inset', !isTouch && 'no-touch', itemsMaxHeight && 'items-max-height', fullImageGalleryType && 'full-image-gallery-type', innerSpacing && 'has-inner-spacing', pagingPersistent && 'has-persistent-nav', className, !!size && typeof window !== 'undefined' ? size : 'sm');
    var carouselContainerClassNames = getClassNameList('uitk-carousel-container', fullImageGalleryType && 'full-image-gallery-type', (SCROLL_WITHOUT_ANIMATION.current || CHANGE_INDEX_WITHOUT_ANIMATION.current) && 'no-animation-on-scroll', naturalWidthItems && 'has-natural-width-items', useContainerSizing && 'use-container-sizing');
    var navProps = {
      analytics: analytics,
      buttonText: buttonText,
      carouselName: carouselName,
      currentIndex: currentIndex,
      itemCount: itemCount,
      itemsVisible: itemsVisible,
      fullSheetVisible: fullSheetVisible,
      navigationIconId: navigationIconId,
      onNextCb: onNextCb,
      onPrevCb: onPrevCb,
      pagingOverlay: pagingOverlay,
      scrollEventOngoing: scrollEventOngoing,
      size: size,
      isRtl: isRtl,
      naturalWidthItems: naturalWidthItems,
      carouselContainerRef: containerRef,
      renderPrevNavButton: currentIndex > 0,
      renderNextNavButton: fullImageGalleryType ? currentIndex < itemCount - 1 : !isFullyScrolled
    };
    var CarouselHeaderLevel = carouselHeaderLevel && UitkHeadingTags.get(carouselHeaderLevel) || UitkHeadingTags.get('h3');
    var shouldRenderNav = checkNavigationShouldBeVisible();
    // A custom prop for each size value (sm,md,etc) -- this is verbose, and these could be instead dynamically changed with JS;
    // but doing it the verbose way ensures that the carousel will always have a value for each breakpoint without hydration,
    // and removes the potential for a layout shift that otherwise can occur prior to full load/hydration
    var cssCustomProperties = __assign(__assign({
      '--uitk-carousel-items-gap': getGapCssCustomProp()
    }, innerSpacing && __assign({
      '--uitk-carousel-inner-spacing': "".concat(innerSpacingAmount, "px")
    }, getCssCustomPropertiesForBreakpointBasedProp('inner-spacing', innerSpacing))), !fullImageGalleryType && !naturalWidthItems && __assign(__assign(__assign({}, getCssCustomPropertiesForBreakpointBasedProp('items-gap', itemsGap)), getCssCustomPropertiesForBreakpointBasedProp('peek-amount', fullPeekAmountObj)), getCssCustomPropertiesForBreakpointBasedProp('item-count', itemsVisible, false)));
    return React.createElement(UitkContextProvider, {
      config: {
        context: 'uitk-carousel-context',
        data: contextData
      }
    }, React.createElement("div", {
      className: cssClassNames,
      ref: wrapperRef,
      "data-stid": "carousel-wrapper"
    }, carouselHeaderText && React.createElement(CarouselHeaderLevel, {
      className: getClassNameList((_a = {}, _a["is-visually-hidden"] = !carouselHeaderVisible, _a))
    }, carouselHeaderText), React.createElement("div", {
      className: carouselContainerClassNames,
      "aria-label": carouselHeaderText,
      ref: containerRef,
      onScroll: onScroll,
      "data-stid": "carousel-container",
      style: cssCustomProperties,
      // eslint-disable-next-line
      tabIndex: 0
    }, React.createElement(UitkCarouselItemList, {
      itemsVisible: itemsVisible,
      size: size,
      currentIndex: currentIndex,
      l10n: l10n,
      peek: peek,
      wrapItems: naturalWidthItems || fullImageGalleryType ? true : wrapItems,
      itemCount: itemCount,
      stretchToFill: stretchToFill
    }, children)), !hideNav && shouldRenderNav && React.createElement(UitkCarouselNav, __assign({}, navProps))));
  };
  React.useImperativeHandle(carouselRef, function () {
    return {
      changeIndex: function (index) {
        return changeIndex(index);
      }
    };
  });
  // Carousel main functions -- Ends
  return renderCarousel();
};
var UitkCarousel = function (props) {
  var l10n = useLocalization();
  return React.createElement(Carousel, __assign({
    l10n: l10n
  }, props));
};
UitkCarousel.displayName = 'UitkCarousel';
export { Carousel, UitkCarousel, UitkCarouselLocalAnalyticsIdType };